/**
 * Härd-CRUD-klient för Lyktan (S-04-13).
 *
 * Wrappar `/api/admin/hearths`-endpointerna (S-04-06):
 *  - `GET /api/admin/hearths` — lista
 *  - `POST /api/admin/hearths` — skapa
 *  - `PUT /api/admin/hearths/{id}` — uppdatera (namn, prioritet, aktivt-fönster)
 *  - `POST /api/admin/hearths/{id}/deactivate` — soft-delete (audit-trail i `admin_actions`)
 *  - `POST /api/admin/hearths/{id}/activate` — återaktivera
 *
 * Backend exponerar inte hård-DELETE (CLAUDE.md hard-rule: aldrig hård-delete
 * av användardata utan audit-trail). "Ta bort härd"-knappen i UI mappar
 * därför till deactivate-endpointen.
 *
 * Hard-rules som tillämpas:
 *  - Cookie-baserad auth via `credentials: 'include'`. Inga tokens i body
 *    eller localStorage/sessionStorage.
 *  - CSRF-header på alla muterande anrop (POST/PUT) — speglar
 *    `harden_admin_csrf`-cookien (S-12-11c). GET-listning är inte
 *    CSRF-skyddad på backend (SameSite=Strict räcker för GET).
 *  - Inga fritext-fält i request-body. `name` är max 100 tecken och tolkas
 *    som kategorisk identifierare, inte fritext (samma kontrakt som
 *    `Hearth.name`).
 *  - Backend returnerar UUID som hearth-id — inga råa databas-ID:n.
 *  - Felmeddelanden mappas till svenska användartexter; backend-felkoderna
 *    är opaka tokens och loggas inte vidare.
 */

import { csrfHeader } from '../auth/csrf';

/** Backend-prioriteter som DB-värden (matchar `HearthPriority.dbValue()`). */
export type HearthPriority = 'low' | 'medium' | 'high';

export type HearthRecord = {
  id: string;
  name: string;
  /**
   * Well-Known Text-representation av geometrin
   * (`MULTILINESTRING((lng lat, lng lat), ...)`). Används bara för
   * uppdateringar — UI:t ritar inte om geometrin från denna sträng,
   * den bara skickas tillbaka oförändrad i PUT-bodyn.
   */
  geometryWkt: string;
  priority: HearthPriority;
  /** ISO-8601 timestamp när härden började gälla. */
  activeFrom: string;
  /** ISO-8601 timestamp när härden upphörde. `null` = aktiv. */
  activeTo: string | null;
  /** Opaque user-UUID för admin som skapade härden. */
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /**
   * Admin-valbar grundfärg (S-04-24). Kortnamn från låst palett — se
   * `lib/util/colorPalette.ts` för de 8 tillåtna värdena. NULL =
   * priority-baserad render-fallback (backward-compat för existerande
   * rundor).
   */
  displayColor: string | null;
};

export type HearthListResult =
  | { kind: 'ok'; items: HearthRecord[]; limit: number; offset: number }
  | { kind: 'unauthorized' }
  | { kind: 'failure'; message: string };

export type HearthMutationResult =
  | { kind: 'ok'; record: HearthRecord }
  | { kind: 'unauthorized' }
  | { kind: 'not_found' }
  | { kind: 'failure'; message: string };

/**
 * Request-body för `POST /api/admin/hearths`. `geometryWkt` är obligatoriskt
 * — geometri sätts vid skapandet och kan inte ändras post-create utan att
 * ny härd skapas (UI varnar om detta). `activeFrom` är optional — backend
 * default:ar till nu.
 */
export type CreateHearthInput = {
  name: string;
  geometryWkt: string;
  priority: HearthPriority;
  activeFrom?: string | null;
  activeTo?: string | null;
  /** Kortnamn från palett-listan i `colorPalette.ts`, eller null. */
  displayColor?: string | null;
};

/**
 * Request-body för `PUT /api/admin/hearths/{id}`. Backend tillåter
 * geometri-uppdatering på endpoint-nivå men UI:t skickar alltid
 * oförändrad geometri (geometri = read-only post-create per UI-kontrakt).
 */
export type UpdateHearthInput = {
  name: string;
  geometryWkt: string;
  priority: HearthPriority;
  activeFrom: string;
  activeTo?: string | null;
  /** Kortnamn från palett-listan eller null för att rensa till fallback. */
  displayColor?: string | null;
};

// Domän-rebranding (S-02-09 + S-02-10a). Backend registrerar samma
// CRUD-handler under två paths under övergångsperioden — `/api/admin/routes`
// är den nya domänpathen, `/api/admin/hearths` är legacy. Lyktan-klienten
// migrerar i denna story (S-04-19) till ny path så att audit-rader som
// härrör från Lyktan får action_type-prefix `route_*` istället för
// `hearth_*`. Mobil-klienter migrerar separat.
const API_BASE = '/api/admin/routes';

function messageForError(code: string): string {
  switch (code) {
    case 'unauthorized':
      return 'Sessionen har gått ut. Logga in igen.';
    case 'forbidden':
      return 'Saknar behörighet att hantera rundor.';
    case 'invalid_name':
      return 'Namnet måste vara mellan 1 och 100 tecken.';
    case 'invalid_priority':
      return 'Ogiltig prioritet. Tillåtet: låg, medel, hög.';
    case 'invalid_active_from':
    case 'invalid_active_to':
    case 'invalid_active_period':
      return 'Ogiltigt aktiveringsfönster. Slut måste vara efter start.';
    case 'invalid_pagination':
      return 'Ogiltig sidnumrering.';
    case 'invalid_id':
      return 'Ogiltigt id.';
    case 'invalid_payload':
      return 'Geometrin kunde inte sparas. Rita om rundan och försök igen.';
    case 'invalid_request_body':
      return 'Ogiltig begäran. Kontrollera fälten och försök igen.';
    case 'not_found':
      return 'Härden kunde inte hittas.';
    case 'network':
      return 'Kunde inte nå servern. Kontrollera anslutningen.';
    default:
      return 'Något gick fel. Försök igen om en stund.';
  }
}

async function safeJson(response: Response): Promise<{ error?: string } | null> {
  try {
    return (await response.json()) as { error?: string };
  } catch {
    return null;
  }
}

type RawHearth = {
  id?: unknown;
  name?: unknown;
  geometry_wkt?: unknown;
  priority?: unknown;
  active_from?: unknown;
  active_to?: unknown;
  created_by?: unknown;
  created_at?: unknown;
  updated_at?: unknown;
  display_color?: unknown;
};

type RawListResponse = {
  items?: unknown;
  limit?: unknown;
  offset?: unknown;
};

function isPriority(v: unknown): v is HearthPriority {
  return v === 'low' || v === 'medium' || v === 'high';
}

/**
 * Defensiv parser: returnerar `null` om JSON inte matchar förväntad form.
 * Vi vill aldrig krascha vid en oväntad payload — UI:t mappar `null` till
 * generisk felmeddelande.
 */
function parseHearth(raw: RawHearth): HearthRecord | null {
  if (
    typeof raw.id !== 'string' ||
    typeof raw.name !== 'string' ||
    typeof raw.geometry_wkt !== 'string' ||
    !isPriority(raw.priority) ||
    typeof raw.active_from !== 'string' ||
    (raw.active_to !== null && typeof raw.active_to !== 'string') ||
    typeof raw.created_by !== 'string' ||
    typeof raw.created_at !== 'string' ||
    typeof raw.updated_at !== 'string'
  ) {
    return null;
  }
  return {
    id: raw.id,
    name: raw.name,
    geometryWkt: raw.geometry_wkt,
    priority: raw.priority,
    activeFrom: raw.active_from,
    activeTo: raw.active_to as string | null,
    createdBy: raw.created_by,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    displayColor: typeof raw.display_color === 'string' ? raw.display_color : null,
  };
}

async function readMutationResponse(response: Response): Promise<HearthMutationResult> {
  if (response.status === 401 || response.status === 403) {
    return { kind: 'unauthorized' };
  }
  if (response.status === 404) {
    return { kind: 'not_found' };
  }
  if (!response.ok) {
    const body = await safeJson(response);
    return { kind: 'failure', message: messageForError(body?.error ?? 'default') };
  }
  let raw: RawHearth;
  try {
    raw = (await response.json()) as RawHearth;
  } catch {
    return { kind: 'failure', message: messageForError('default') };
  }
  const parsed = parseHearth(raw);
  if (!parsed) {
    return { kind: 'failure', message: messageForError('default') };
  }
  return { kind: 'ok', record: parsed };
}

/**
 * GET /api/admin/hearths — listar alla härdar (aktiva + inaktiva).
 *
 * Backend pagerar med limit/offset (default 50, max 200). Lyktan-UI:t
 * begär hela första sidan och låter admin filtrera klient-side.
 */
export async function listHearths(
  params: { limit?: number; offset?: number } = {},
): Promise<HearthListResult> {
  const search = new URLSearchParams();
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.offset !== undefined) search.set('offset', String(params.offset));
  const url = search.toString().length > 0 ? `${API_BASE}?${search.toString()}` : API_BASE;

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 401 || response.status === 403) {
    return { kind: 'unauthorized' };
  }
  if (!response.ok) {
    const body = await safeJson(response);
    return { kind: 'failure', message: messageForError(body?.error ?? 'default') };
  }

  let body: RawListResponse;
  try {
    body = (await response.json()) as RawListResponse;
  } catch {
    return { kind: 'failure', message: messageForError('default') };
  }

  if (
    !Array.isArray(body.items) ||
    typeof body.limit !== 'number' ||
    typeof body.offset !== 'number'
  ) {
    return { kind: 'failure', message: messageForError('default') };
  }

  const items: HearthRecord[] = [];
  for (const raw of body.items) {
    const parsed = parseHearth(raw as RawHearth);
    if (!parsed) {
      return { kind: 'failure', message: messageForError('default') };
    }
    items.push(parsed);
  }
  return { kind: 'ok', items, limit: body.limit, offset: body.offset };
}

/**
 * POST /api/admin/hearths — skapar ny härd.
 *
 * Returnerar 201 + den skapade härden. Backend skriver audit-rad
 * `hearth_create` automatiskt (S-04-06).
 */
export async function createHearth(input: CreateHearthInput): Promise<HearthMutationResult> {
  const body = JSON.stringify({
    name: input.name,
    geometry_wkt: input.geometryWkt,
    priority: input.priority,
    active_from: input.activeFrom ?? null,
    active_to: input.activeTo ?? null,
    display_color: input.displayColor ?? null,
  });

  let response: Response;
  try {
    response = await fetch(API_BASE, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...csrfHeader() },
      body,
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  return readMutationResponse(response);
}

/**
 * PUT /api/admin/hearths/{id} — uppdaterar muterbara fält. Geometrin
 * skickas oförändrad eftersom UI:t inte tillåter geometri-redigering
 * post-create (samma härd-id ska peka på samma fysiska yta över tid).
 */
export async function updateHearth(
  id: string,
  input: UpdateHearthInput,
): Promise<HearthMutationResult> {
  const body = JSON.stringify({
    name: input.name,
    geometry_wkt: input.geometryWkt,
    priority: input.priority,
    active_from: input.activeFrom,
    active_to: input.activeTo ?? null,
    display_color: input.displayColor ?? null,
  });

  let response: Response;
  try {
    response = await fetch(`${API_BASE}/${encodeURIComponent(id)}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...csrfHeader() },
      body,
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  return readMutationResponse(response);
}

/**
 * POST /api/admin/hearths/{id}/deactivate — soft-delete via audit-trail.
 *
 * Backend sätter `active_to=NOW()` och skriver `hearth_deactivate`-rad i
 * `admin_actions`. Härden visas fortfarande i listan men markeras som
 * inaktiv. Hård-DELETE finns inte (CLAUDE.md hard-rule).
 */
export async function deactivateHearth(id: string): Promise<HearthMutationResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/${encodeURIComponent(id)}/deactivate`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...csrfHeader() },
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }
  return readMutationResponse(response);
}

/**
 * POST /api/admin/hearths/{id}/activate — återaktivera tidigare inaktiverad
 * härd. Backend nollställer `active_to`. Audit-rad `hearth_activate`.
 */
export async function activateHearth(id: string): Promise<HearthMutationResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/${encodeURIComponent(id)}/activate`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...csrfHeader() },
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }
  return readMutationResponse(response);
}

/**
 * Avgör om en härd är aktiv just nu. En härd är aktiv om `active_to`
 * är `null` eller framåt i tiden. Hjälpare så vy-koden inte behöver
 * datum-jämförelse på tre ställen.
 */
export function isActive(record: HearthRecord, now: Date = new Date()): boolean {
  if (record.activeTo === null) return true;
  const end = new Date(record.activeTo);
  if (Number.isNaN(end.getTime())) return true;
  return end.getTime() > now.getTime();
}

/** Svensk etikett för prioritet. UI-text på svenska enligt CLAUDE.md. */
export function priorityLabel(priority: HearthPriority): string {
  switch (priority) {
    case 'low':
      return 'Låg';
    case 'medium':
      return 'Medel';
    case 'high':
      return 'Hög';
  }
}
